import { Box, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BaseRow from '~/components/common/base-row/BaseRow';
import { handleApiError } from '~/utils/helper';
import { BaseScrollbar } from '~/components/common';
import notificationApi from '~/apis/notification';
import { IAlert } from '~/utils/interface/alert';
import {
  Alert2Icon,
  AlertIcon,
  BellRingingIcon,
  Close2Icon,
  FireIcon,
  InformationLine3Icon,
  InformationLineIcon,
} from '~/assets/icons';
import classNames from 'classnames/bind';
import styles from '../Dashboard.module.scss';
import ConfirmDialog from '~/components/common/confirm-dialog/ConfirmDialog';
import TransferDistributorDialog from '../../distributor/component/transfer-distributor-dialog/TransferDistributorDialog';

type NotificationProps = {
  onSetAnchorElement: (value: any) => void;
};

const cx = classNames.bind(styles);

const Notification = (props: NotificationProps) => {
  const { t } = useTranslation('admin/alert');

  const [alert, setAlert] = useState<IAlert>();
  const [alertList, setAlertList] = useState<IAlert[]>([]);
  const [filteredAlertList, setFilteredAlertList] = useState<IAlert[]>([]);

  const [isOpenConfirmDistributorDialog, setIsOpenConfirmDistributorDialog] = useState<boolean>(false);
  const [isOpenTransferDistributorDialog, setIsOpenTransferDistributorDialog] = useState<boolean>(false);

  const setAlertLeverRef = useRef<number>(0);

  useEffect(() => {
    (async () => {
      try {
        const response = await notificationApi.listAlert();
        setAlertList(response.data);
        setFilteredAlertList(response.data);
      } catch (error) {
        handleApiError(error);
      }
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getAlertObject = (alert_subject: number): string => {
    if (alert_subject === 1) return t('alert_subject_options.warning');
    if (alert_subject === 2) return t('alert_subject_options.device');
    if (alert_subject === 3) return t('alert_subject_options.user');
    return t('alert_subject_options.agentDistributor');
  };

  const handleClickAlert = async (alert_lever: number) => {
    setAlertLeverRef.current = alert_lever;

    let filteredAlerts: IAlert[] = [];

    if (alert_lever === 0) filteredAlerts = alertList;
    else filteredAlerts = alertList.filter((item) => item.alert_lever === alert_lever);

    setFilteredAlertList(filteredAlerts);
  };

  const handleClickRow = (row: IAlert) => {
    if (row.alert_type === 10) {
      setIsOpenConfirmDistributorDialog(true);
      setAlert(row);
    }
  };

  const handleConfirmationDistributor = () => {
    setIsOpenConfirmDistributorDialog(false);
    setIsOpenTransferDistributorDialog(true);
  };

  const renderInProgressAlert = (row: IAlert) => {
    if (row.alert_subject === 1 && row.status === 0) {
      return (
        <BaseRow className='justify-center border rounded-[8px] p-2.5 gap-3 hover:opacity-80 relative bg-[#ED1002]'>
          <Box className='flex flex-col justify-between items-center'>
            <FireIcon className='w-11 h-11 mb-1.5' />
            <Typography
              fontWeight={'600'}
              className='text-[#ED1002] bg-white rounded-full py-1.5 px-2 font-bold whitespace-nowrap'
            >
              {t('status_options.inProgress')}
            </Typography>
          </Box>
          <Box className='w-64 m-auto text-white'>
            <Typography fontWeight={'700'} fontSize={'14px'}>
              {getAlertObject(row.alert_subject)}
            </Typography>
            <Typography className='truncate my-1'>{row.content}</Typography>
            <Typography className='truncate'>{row.alert_object}</Typography>
            <Box className='absolute bottom-0 right-2'>
              <Typography whiteSpace={'nowrap'}>
                {row.created_at ? dayjs(row.created_at).format('HH:mm - DD/MM/YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </BaseRow>
      );
    } else if (row.status === 0) {
      return (
        <BaseRow className='justify-center border rounded-[8px] p-2.5 gap-3 hover:opacity-80 relative bg-[#ED1002]'>
          <Box className='flex flex-col justify-between items-center'>
            <AlertIcon className='w-11 h-11 mb-1.5' />
            <Typography
              fontWeight={'600'}
              className='text-[#ED1002] bg-white rounded-full py-1.5 px-3.5 font-bold whitespace-nowrap'
            >
              {t('status_options.unProcessed')}
            </Typography>
          </Box>
          <Box className='w-64 m-auto text-white'>
            <Typography fontWeight={'700'} fontSize={'14px'}>
              {getAlertObject(row.alert_subject)}
            </Typography>
            <Typography className='truncate my-1'>{row.content}</Typography>
            <Typography className='truncate'>{row.alert_object}</Typography>
            <Box className='absolute bottom-0 right-2'>
              <Typography whiteSpace={'nowrap'}>
                {row.created_at ? dayjs(row.created_at).format('HH:mm - DD/MM/YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </BaseRow>
      );
    } else if ([1, 2, 3].includes(row.status)) {
      return (
        <BaseRow className='justify-center border rounded-[8px] p-2.5 gap-3 hover:opacity-80 relative bg-white border-[#ED1002]'>
          <Box className='flex flex-col justify-between items-center'>
            <Alert2Icon className='w-11 h-11 mb-1.5' />
            <Typography
              fontWeight={'600'}
              className='bg-[#ED1002] text-white rounded-full py-1.5 px-6 font-bold whitespace-nowrap'
            >
              {t('status_options.processed')}
            </Typography>
          </Box>
          <Box className='w-64 m-auto text-[#ED1002]'>
            <Typography fontWeight={'700'} fontSize={'14px'}>
              {getAlertObject(row.alert_subject)}
            </Typography>
            <Typography className='truncate my-1'>{row.content}</Typography>
            <Typography className='truncate'>{row.alert_object}</Typography>
            <Box className='absolute bottom-0 right-2'>
              <Typography whiteSpace={'nowrap'}>
                {row.created_at ? dayjs(row.created_at).format('HH:mm - DD/MM/YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </BaseRow>
      );
    } else return null;
  };

  const renderWaringAlert = (row: IAlert) => {
    if (row.status === 0) {
      return (
        <BaseRow className='justify-center rounded-[8px] p-2.5 gap-3 hover:opacity-80 relative bg-[#FF9500]'>
          <Box className='flex flex-col items-center'>
            <InformationLineIcon className='w-9 h-9 mb-1.5' fill='white' />
            <Typography
              fontWeight={'600'}
              className='text-[#FF9500] bg-white rounded-full py-1.5 px-4 font-bold whitespace-nowrap'
            >
              {t('status_options.unProcessed')}
            </Typography>
          </Box>
          <Box className='w-64 m-auto text-white'>
            <Typography fontWeight={'700'} fontSize={'14px'}>
              {getAlertObject(row.alert_subject)}
            </Typography>
            <Typography className='truncate my-1'>{row.content}</Typography>
            <Typography className='truncate'>{row.alert_object}</Typography>
            <Box className='absolute bottom-0 right-2'>
              <Typography whiteSpace={'nowrap'}>
                {row.created_at ? dayjs(row.created_at).format('HH:mm - DD/MM/YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </BaseRow>
      );
    } else if ([1, 2, 3].includes(row.status)) {
      return (
        <BaseRow className='justify-center rounded-[8px] p-2.5 gap-3 hover:opacity-80 relative border-[#FF9500] bg-white'>
          <Box className='flex flex-col items-center'>
            <InformationLine3Icon className='w-9 h-9 mb-1.5' fill='#FF9500' />
            <Typography
              fontWeight={'600'}
              className='text-white bg-[#FF9500] rounded-full py-1.5 px-6 font-bold whitespace-nowrap'
            >
              {t('status_options.processed')}
            </Typography>
          </Box>
          <Box className='w-64 m-auto text-[#FF9500]'>
            <Typography fontWeight={'700'} fontSize={'14px'}>
              {getAlertObject(row.alert_subject)}
            </Typography>
            <Typography className='truncate my-1'>{row.content}</Typography>
            <Typography className='truncate'>{row.alert_object}</Typography>
            <Box className='absolute bottom-0 right-2'>
              <Typography whiteSpace={'nowrap'}>
                {row.created_at ? dayjs(row.created_at).format('HH:mm - DD/MM/YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </BaseRow>
      );
    } else return null;
  };

  const renderContent = (row: IAlert) => {
    if (row.alert_lever === 3) {
      return renderInProgressAlert(row);
    } else if (row.alert_lever === 2) {
      return renderWaringAlert(row);
    } else {
      return (
        <BaseRow className='bg-white justify-center !items-center rounded-[8px] p-2.5 gap-3 hover:opacity-80 relative'>
          <Box className='flex flex-col items-center basis-1/4'>
            <BellRingingIcon className='w-11 h-11' />
          </Box>
          <Box className='w-[250px] m-auto'>
            <Typography color={'black'} fontWeight={'700'} fontSize={'14px'}>
              {getAlertObject(row.alert_subject)}
            </Typography>
            <Typography color={'black'} className='truncate'>
              {row.content}
            </Typography>
            <Typography color={'black'} className='truncate'>
              {row.alert_object}
            </Typography>
            <Box className='absolute bottom-0 right-2'>
              <Typography color={'black'} className='mt-10'>
                {row.created_at ? dayjs(row.created_at).format('HH:mm - DD/MM/YYYY') : ''}
              </Typography>
            </Box>
          </Box>
        </BaseRow>
      );
    }
  };

  return (
    <Box className='w-full'>
      <Box className='relative py-4 px-8 text-center border-b border-[#EEEEEE]'>
        <Typography className='text-[#ED1002] text-[14px] font-bold'>
          {t('alert_lever_options.notification')}
        </Typography>
        <Box
          className='absolute top-3.5 right-3.5 hover:cursor-pointer'
          onClick={() => {
            props.onSetAnchorElement(null);
          }}
        >
          <Close2Icon fill='#DFDFDF' />
        </Box>
      </Box>
      <Box className='bg-[#EEEEEE] p-1.5'>
        <BaseRow className='gap-2 justify-between px-1.5'>
          <Box
            className={`border-[#005F73] border rounded-full font-bold py-2 px-4 hover:cursor-pointer hover:opacity-80 ${
              setAlertLeverRef.current === 0 ? 'bg-[#005F73] text-white' : 'bg-white text-[#005F73]'
            }`}
            onClick={() => handleClickAlert(0)}
          >
            {t('alert_lever_options.all')}
          </Box>
          <Box
            className={`text-[#ED1002] border border-[#ED1002] rounded-full font-bold py-2 px-4 hover:cursor-pointer ${
              setAlertLeverRef.current === 3 ? 'bg-[#ED1002] text-white' : 'bg-white'
            }`}
            onClick={() => handleClickAlert(3)}
          >
            {t('alert_lever_options.critical')}
          </Box>
          <Box
            className={`text-[#FF9500] border border-[#FF9500] rounded-full font-bold py-2 px-4 hover:cursor-pointer ${
              setAlertLeverRef.current === 2 ? 'bg-[#FF9500] text-white' : 'bg-white'
            }`}
            onClick={() => handleClickAlert(2)}
          >
            {t('alert_lever_options.warning')}
          </Box>
          <Box
            className={`text-[#000000] border border-[#000000] rounded-full font-bold py-2 px-4 hover:cursor-pointer ${
              setAlertLeverRef.current === 1 ? 'bg-[#000000] text-white' : 'bg-white'
            }`}
            onClick={() => handleClickAlert(1)}
          >
            {t('alert_lever_options.notification')}
          </Box>
        </BaseRow>
        {filteredAlertList?.length ? (
          <BaseScrollbar maxHeight='600px'>
            <Box className='overflow-hidden'>
              <Table className={cx('notification-table')}>
                <TableBody>
                  {filteredAlertList.map((row, index) => {
                    return (
                      <TableRow key={index}>
                        <TableCell
                          className='border-none hover:cursor-pointer'
                          onClick={() => {
                            handleClickRow(row);
                          }}
                        >
                          {renderContent(row)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </BaseScrollbar>
        ) : null}
      </Box>
      {isOpenConfirmDistributorDialog && (
        <ConfirmDialog
          open={isOpenConfirmDistributorDialog}
          title={t('alert2')}
          description={t('distributor_name', {
            distributor_name: alert?.distributor_name,
          })}
          confirm={t('switch')}
          onConfirm={handleConfirmationDistributor}
          onCancel={() => setIsOpenConfirmDistributorDialog(false)}
        />
      )}

      {isOpenTransferDistributorDialog && (
        <TransferDistributorDialog
          data={alert}
          open={isOpenTransferDistributorDialog}
          setOpen={setIsOpenTransferDistributorDialog}
        />
      )}
    </Box>
  );
};

export default memo(Notification);
